import {createStore, createEvent, forward} from '@effector/effector'
import {
  isPlay,
  play as playEvent,
  pause as pauseEvent
} from '../../runtime/playState'
import {h, data, handler, attr, transform, nodeMethod} from '../../h'
import {runRuntime} from '../../workingSet'
import {generatePath, M, L, Z} from '../../path'

import './index.css'

const keyboardEvent = createEvent<KeyboardEvent>()
const spaceKey = keyboardEvent.filter({
  fn: e => e.code === 'Space'
})

const toggleFullscreen = createEvent<any>()
const fullscreenChanged = createEvent<any>()
const isFullscreen = createStore(!!document.fullscreenElement).on(
  fullscreenChanged,
  () => !!document.fullscreenElement
)
toggleFullscreen.watch(async () => {
  const isFullscreen = !!document.fullscreenElement
  if (!document.fullscreenEnabled) return
  if (isFullscreen) {
    await document.exitFullscreen().catch(err => {
      console.error('Error during fullscreen deactivation')
      console.error(err)
    })
  } else {
    await document.body.requestFullscreen().catch(err => {
      console.error('Error during fullscreen activation')
      console.error(err)
    })
  }
})
const togglePlay = createEvent<any>()
isPlay.on(togglePlay, play => !play)
forward({
  from: spaceKey,
  to: togglePlay
})
const clickRestart = createEvent<any>()
clickRestart.watch(() => {
  requestAnimationFrame(runRuntime)
})
window.addEventListener('keydown', keyboardEvent, true)

document.addEventListener('fullscreenchange', fullscreenChanged)
export function Play() {
  h('section', () => {
    data({section: 'play-control-panel'})
    h('button', () => {
      h('span', () => {
        RestartButton()
      })
      data({
        ui: 'button',
        playControl: 'play'
      })
      handler({click: clickRestart})
      attr({title: 'restart program'})
    })
    h('button', () => {
      h('span', () => {
        PlayPauseIcon()
      })
      data({
        ui: 'button',
        playControl: 'play'
      })
      handler({click: togglePlay})
      attr({title: 'resume or pause program'})
    })
    if (document.fullscreenEnabled)
      h('button', () => {
        h('span', () => {
          FullscreenButton()
        })
        data({
          ui: 'button',
          playControl: 'play'
        })
        handler({click: toggleFullscreen})
        attr({title: 'enable or disable fullscreen mode'})
      })
  })
  spaceKey.watch(e => {
    e.preventDefault()
  })
}
function FullscreenButton() {
  h('svg', () => {
    attr({
      viewBox: '0 0 50 50'
    })
    h('path', () => {
      const duration = '600ms'
      const DELAY = 150
      const height = 50
      const interval = 12
      const width = height
      const lineWidth = (width - interval) / 2
      const offset = lineWidth / 4
      const sndLineStart = lineWidth + interval
      const windowScreen = generatePath(() => {
        M(0, 0)
        L(0, lineWidth)
        L(lineWidth - offset, lineWidth - offset)
        L(lineWidth, 0)
        Z()

        M(0, sndLineStart)
        L(0, height)
        L(lineWidth, height)
        L(lineWidth - offset, sndLineStart + offset)
        Z()

        M(sndLineStart, 0)
        L(sndLineStart + offset, lineWidth - offset)
        L(width, lineWidth)
        L(width, 0)
        Z()

        M(sndLineStart + offset, sndLineStart + offset)
        L(sndLineStart, height)
        L(width, height)
        L(width, sndLineStart)
        Z()
      })
      const fullScreen = generatePath(() => {
        M(offset, offset)
        L(0, lineWidth)
        L(lineWidth, lineWidth)
        L(lineWidth, 0)
        Z()

        M(0, sndLineStart)
        L(offset, height - offset)
        L(lineWidth, height)
        L(lineWidth, sndLineStart)
        Z()

        M(sndLineStart, 0)
        L(sndLineStart, lineWidth)
        L(width, lineWidth)
        L(width - offset, offset)
        Z()

        M(sndLineStart, sndLineStart)
        L(sndLineStart, height)
        L(width - offset, height - offset)
        L(width, sndLineStart)
        Z()
      })

      transform({
        translate: {x: (height - width) / 2}
      })
      attr({d: windowScreen})
      data({playControlIcon: true})
      let animationTimeout: NodeJS.Timeout
      h('animate', () => {
        const beginElement = nodeMethod<SVGAnimateElement, any>('beginElement')
        attr({
          attributeName: 'd',
          dur: duration,
          from: windowScreen,
          to: fullScreen,
          fill: 'freeze',
          begin: 'indefinite'
        })
        isFullscreen.updates.watch(isFullscreen => {
          if (!isFullscreen) return
          clearTimeout(animationTimeout)
          animationTimeout = setTimeout(() => beginElement(), DELAY)
        })
      })
      h('animate', () => {
        const beginElement = nodeMethod<SVGAnimateElement, any>('beginElement')
        attr({
          attributeName: 'd',
          dur: duration,
          from: fullScreen,
          to: windowScreen,
          fill: 'freeze',
          begin: 'indefinite'
        })
        isFullscreen.updates.watch(isFullscreen => {
          if (isFullscreen) return
          clearTimeout(animationTimeout)
          animationTimeout = setTimeout(() => beginElement(), DELAY)
        })
      })
    })
  })
}
function RestartButton() {
  h('svg', () => {
    attr({
      viewBox: '0 0 50 50'
    })
    h('path', () => {
      const interval = 12
      const height = 50
      const width = 0.5 * height * (Math.sqrt(3) / 2)
      const lineWidth = (height * (Math.sqrt(3) / 2) - interval) / 2
      const sndLineStart = width
      const paddingRight = height - (sndLineStart + lineWidth)
      const d = generatePath(() => {
        M(0, height * (2 / 4))
        L(width, height)
        L(width, 0)
        Z()

        M(sndLineStart, 0)
        L(sndLineStart, height)
        L(sndLineStart + lineWidth, height)
        L(sndLineStart + lineWidth, 0)
        Z()
      })
      transform({
        translate: {x: paddingRight / 2}
      })
      attr({d})
      data({playControlIcon: true})
    })
  })
}
function PlayPauseIcon() {
  h('svg', () => {
    attr({
      viewBox: '0 0 50 50'
    })
    h('path', () => {
      const duration = '100ms'
      const interval = 12
      const height = 50
      const width = height * (Math.sqrt(3) / 2)
      const lineWidth = (width - interval) / 2
      const sndLineStart = lineWidth + interval
      const center = lineWidth + interval / 2
      const play = generatePath(() => {
        M(0, 0)
        L(0, height)
        L(center, height * (3 / 4))
        L(center, height * (2 / 4))
        L(center, height * (1 / 4))
        Z()

        M(center, height * (1 / 4))
        L(center, height * (3 / 4))
        L(width, height * (2 / 4))
        L(width, height * (2 / 4))
        Z()
      })
      const pause = generatePath(() => {
        M(0, 0)
        L(0, height)
        L(lineWidth, height)
        L(lineWidth, height / 2)
        L(lineWidth, 0)
        Z()

        M(sndLineStart, 0)
        L(sndLineStart, height)
        L(width, height)
        L(width, 0)
        Z()
      })
      transform({
        translate: {x: (height - width) / 2}
      })
      attr({d: pause})
      data({playControlIcon: true})
      h('animate', () => {
        const beginElement = nodeMethod<SVGAnimateElement, any>('beginElement')
        attr({
          attributeName: 'd',
          dur: duration,
          from: play,
          to: pause,
          fill: 'freeze',
          begin: 'indefinite'
        })
        playEvent.watch(() => {
          beginElement()
        })
      })
      h('animate', () => {
        const beginElement = nodeMethod<SVGAnimateElement, any>('beginElement')
        attr({
          attributeName: 'd',
          dur: duration,
          from: pause,
          to: play,
          fill: 'freeze',
          begin: 'indefinite'
        })
        pauseEvent.watch(() => {
          beginElement()
        })
      })
    })
  })
}
